import { EuiEmptyPrompt, EuiPageTemplate, EuiText } from "@inscopix/ideas-eui";
import { Route, Switch, useParams } from "react-router-dom";
import { Project } from "components/Project/Project";
import { usePageProjectData } from "./usePageProjectData";
import { ProjectDataProvider } from "./ProjectDataProvider";
import { PageProjectDataset } from "./dataset/PageProjectDataset";
import { Page404 } from "pages/404/Page404";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { Helmet } from "react-helmet-async";
import { ProjectFilesManager } from "stores/project-files/ProjectFilesManager";
import { ProjectAccessCallout } from "components/Project/ProjectAccessCallout";
import { ProjectLayoutProvider } from "components/Project/ProjectLayoutProvider";
import { useEffect, useState } from "react";
import { pollAnalysisResults } from "./PageProject.helpers";
import { CallOutQuotas } from "components/CallOutQuotas/CallOutQuotas";
import { ToolSpecProvider } from "providers/ToolSpecProvider/ToolSpecProvider";
import { CallOutContract } from "components/CallOutContract/CallOutContract";
import { ProjectStatus } from "types/constants";
import { ideasFeatures } from "ideas.features";
import { PageGenericDataTables } from "pages/gdt/PageGenericDataTables";
import { ProjectAnalysisRoutes } from "pages/routes/ProjectAnalysisRoutes";
import { RecordingIdentifierProvider } from "providers/RecordingIdentifierProvider/RecordingIdentifierProvider";
import { ProjectPermissionProvider } from "./ProjectPermissionProvider";
import { PageProject404 } from "./PageProject404";
import { ModalProvider } from "providers/ModalProvider/ModalProvider";
import { DataTableProvider } from "pages/gdt/store/DataTableProvider";
import { ProjectFoldersProvider } from "pages/gdt/folders/ProjectFoldersProvider";
import AppLoading from "components/AppLoading/AppLoading";
import { uuid } from "utils/uuid";

export const PageProject = () => {
  const projectKey = useParams<{ projectKey: string }>().projectKey;
  const tableKey = useParams<{ tableKey: string | undefined }>().tableKey;
  const routeMap = useRouteMapContext((s) => s.routeMap);

  // Generate a unique key for the DataTableProvider to force re-rendering
  // and reset the store, ie. when sync status is error
  const [dataTableProviderKey, setDataTableProviderKey] = useState(uuid());
  const resetDataTableProviderKey = () => {
    setDataTableProviderKey(uuid());
  };

  // Poll for analysis results until the component unmounts
  useEffect(() => {
    const stopPolling = pollAnalysisResults(projectKey);
    return stopPolling;
  }, [projectKey]);

  const { loading, error, data } = usePageProjectData({ projectKey });

  if (loading && data === undefined) {
    return <AppLoading />;
  }

  if (error) {
    return <PageProject404 projectKey={projectKey} />;
  }

  const { project } = data;

  if (project.isToolProject) {
    return <Page404 />;
  }

  if (project.status === ProjectStatus.CLONING) {
    return (
      <>
        <Helmet>
          <title>{project.name}</title>
        </Helmet>
        <EuiPageTemplate>
          <EuiPageTemplate>
            <EuiEmptyPrompt
              iconType="copy"
              title={
                <EuiText>
                  <h1>Copying in progress</h1>
                </EuiText>
              }
              body={
                <EuiText>
                  <p>
                    The project you are trying to access is in the process of
                    being copied. Please check back later.
                  </p>
                </EuiText>
              }
            />
          </EuiPageTemplate>
        </EuiPageTemplate>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${project.name}`}</title>
      </Helmet>
      <Switch>
        <ToolSpecProvider>
          <ProjectDataProvider
            {...data}
            resetDataTableProviderKey={resetDataTableProviderKey}
          >
            <ProjectPermissionProvider projectId={project.id}>
              {ideasFeatures.genericDataTables ? (
                <DataTableProvider
                  key={dataTableProviderKey}
                  projectKey={projectKey}
                  tableKey={tableKey}
                >
                  <ProjectFoldersProvider projectKey={projectKey}>
                    <ModalProvider>
                      <PageGenericDataTables />
                    </ModalProvider>
                  </ProjectFoldersProvider>
                </DataTableProvider>
              ) : (
                <RecordingIdentifierProvider>
                  <ProjectAccessCallout>
                    <EuiPageTemplate style={{ maxHeight: "100vh" }}>
                      <CallOutContract format="call_out" />
                      <CallOutQuotas />
                      <Route
                        path={
                          routeMap["PROJECT_ANALYSIS_TABLE_GROUP"]
                            .parameterizedPath
                        }
                        exact
                      >
                        <ProjectFilesManager projectId={project.id}>
                          <ProjectAnalysisRoutes
                            projectId={project.id}
                            projectKey={project.key}
                          />
                        </ProjectFilesManager>
                      </Route>
                      <Route path={routeMap.DATASET.parameterizedPath}>
                        <PageProjectDataset />
                      </Route>
                      <Route path={routeMap.PROJECT.parameterizedPath} exact>
                        <ProjectFilesManager projectId={project.id}>
                          <ModalProvider>
                            <ProjectLayoutProvider>
                              <Project />
                            </ProjectLayoutProvider>
                          </ModalProvider>
                        </ProjectFilesManager>
                      </Route>
                    </EuiPageTemplate>
                  </ProjectAccessCallout>
                </RecordingIdentifierProvider>
              )}
            </ProjectPermissionProvider>
          </ProjectDataProvider>
        </ToolSpecProvider>
        <Route path="*">
          <Page404 />
        </Route>
      </Switch>
    </>
  );
};
